/**
 * Industry case studies for marketing pages.
 * Visual language matches Landing (yellow / black / white).
 */

export const CASE_STUDY_BRAND = 'Snagcart';

export const industryCaseStudies = [
  {
    slug: 'restaurants',
    industry: 'Restaurants',
    eyebrow: 'Hospitality',
    headline: 'Fill every table without juggling five apps',
    summary:
      'Online orders, WhatsApp campaigns, booking, and a stunning menu site — run from one AI workspace.',
    heroAccent: '#fbbf0f',
    metricHighlight: { value: '38%', label: 'more repeat orders in 90 days' },
    challenge: {
      title: 'The dinner rush shouldn’t break your ops',
      body:
        'Most restaurants stitch together a website builder, POS notes, Instagram DMs, and a separate booking tool. Guests fall through the cracks; staff waste time switching tabs instead of serving tables.',
      pains: [
        'Menu updates take days and look inconsistent',
        'Reservations and takeout live in different inboxes',
        'No simple way to re-engage guests who haven’t visited',
        'Marketing creatives cost more than they convert',
      ],
    },
    solution: {
      title: 'One kitchen for your digital brand',
      body:
        `${CASE_STUDY_BRAND} gives restaurants a branded site, ordering flow, AI content, WhatsApp blasts, and lead capture — so every guest touchpoint feels intentional.`,
      pillars: [
        {
          title: 'Menu-ready websites',
          desc: 'Launch a beautiful site with menus, hours, and location in hours — not weeks.',
        },
        {
          title: 'WhatsApp & SMS fills',
          desc: 'Promote today’s specials and win back quiet weeknights with one-tap campaigns.',
        },
        {
          title: 'AI creative on demand',
          desc: 'Generate dish photos, reels scripts, and social captions without a designer on payroll.',
        },
        {
          title: 'Leads that book tables',
          desc: 'Capture catering inquiries and private events into a simple CRM pipeline.',
        },
      ],
    },
    results: [
      { value: '38%', label: 'Lift in repeat orders' },
      { value: '3×', label: 'Faster menu updates' },
      { value: '12 hrs', label: 'Saved on marketing / week' },
      { value: '4.8★', label: 'Guest booking experience' },
    ],
    stack: ['AI Website Builder', 'WhatsApp Marketing', 'Media Studio', 'CRM & Leads', 'Email Marketing'],
    story: {
      company: 'Harbor & Hearth Bistro',
      role: 'Owner-operator',
      quote:
        'We stopped paying for three tools and a freelance designer. Specials go live before lunch service, and WhatsApp brings people back on slow Tuesdays.',
      outcome: 'Doubled catering inquiries while cutting tool spend by half.',
    },
    playbook: [
      { step: '01', title: 'Publish the brand site', desc: 'AI builds a menu-first site with booking and contact.' },
      { step: '02', title: 'Connect channels', desc: 'WhatsApp, email, and social creative share one brand kit.' },
      { step: '03', title: 'Automate the fills', desc: 'Win-back sequences and event campaigns run on autopilot.' },
    ],
  },
  {
    slug: 'ecommerce',
    industry: 'E-commerce',
    eyebrow: 'Retail & DTC',
    headline: 'Ship a store that sells while you sleep',
    summary:
      'Product pages, AI photography, abandoned-cart journeys, and ads creative — unified for growing brands.',
    heroAccent: '#fbbf0f',
    metricHighlight: { value: '2.4×', label: 'ROAS on relaunched campaigns' },
    challenge: {
      title: 'Your stack grows faster than your revenue',
      body:
        'DTC teams bounce between storefronts, email tools, ad creatives, and spreadsheets. Launch velocity drops; creative fatigue kills ROAS.',
      pains: [
        'Product shoots delay every drop',
        'Cart recovery lives in a separate ESP',
        'Landing pages never match the brand kit',
        'Ops can’t see which campaigns actually convert',
      ],
    },
    solution: {
      title: 'Storefront + growth engine, same login',
      body:
        `${CASE_STUDY_BRAND} pairs commerce-ready sites with Media Studio, marketing automation, and CRM so every SKU launch has pages, creatives, and follow-ups ready together.`,
      pillars: [
        {
          title: 'Conversion-ready pages',
          desc: 'AI sites and templates tuned for product storytelling and checkout clarity.',
        },
        {
          title: 'AI product photography',
          desc: 'Studio-quality shots and variants without waiting on a photoshoot.',
        },
        {
          title: 'Lifecycle messaging',
          desc: 'Email, SMS, and WhatsApp journeys that recover carts and drive repeats.',
        },
        {
          title: 'Performance clarity',
          desc: 'See leads, orders, and campaign outcomes in one workspace.',
        },
      ],
    },
    results: [
      { value: '2.4×', label: 'Campaign ROAS' },
      { value: '27%', label: 'Fewer abandoned carts' },
      { value: '5 days', label: 'From idea to live drop' },
      { value: '40%', label: 'Less creative spend' },
    ],
    stack: ['E-commerce', 'Media Studio', 'Email Marketing', 'AI Ads', 'Automations'],
    story: {
      company: 'Northline Goods',
      role: 'Head of Growth',
      quote:
        'We relaunched our catalog with AI photography and synced WhatsApp + email. Same team, dramatically better velocity.',
      outcome: 'Cut creative production time from weeks to days.',
    },
    playbook: [
      { step: '01', title: 'Build the storefront', desc: 'Launch product pages and brand story in one pass.' },
      { step: '02', title: 'Generate the drop kit', desc: 'Images, ads copy, and social packs from Media Studio.' },
      { step: '03', title: 'Automate recovery', desc: 'Cart and post-purchase flows keep revenue compounding.' },
    ],
  },
  {
    slug: 'agencies',
    industry: 'Agencies',
    eyebrow: 'White-label',
    headline: 'Deliver client work at agency speed',
    summary:
      'White-label sites, AI tools, and multi-channel campaigns — so you ship retainers without hiring a new pod every quarter.',
    heroAccent: '#fbbf0f',
    metricHighlight: { value: '60%', label: 'faster client launches' },
    challenge: {
      title: 'Retainers shouldn’t mean reinventing the wheel',
      body:
        'Agencies lose margin stitching freelancers, no-code tools, and ad-hoc AI accounts per client. Delivery is inconsistent and hard to scale.',
      pains: [
        'Every client needs a different tool stack',
        'White-label options look generic or leak your brand',
        'Reporting is a Friday-night scramble',
        'Junior staff can’t ship without senior babysitting',
      ],
    },
    solution: {
      title: 'A production OS for client brands',
      body:
        `${CASE_STUDY_BRAND} lets agencies spin branded workspaces, ship sites and campaigns, and keep AI tools under one roof — with white-label ready surfaces.`,
      pillars: [
        {
          title: 'White-label delivery',
          desc: 'Present a polished brand experience without exposing the underlying stack.',
        },
        {
          title: 'Repeatable site builds',
          desc: 'AI builder + templates turn briefs into live previews in a single session.',
        },
        {
          title: 'Campaign factory',
          desc: 'WhatsApp, email, SEO, and creatives reuse the same brand assets.',
        },
        {
          title: 'Margin-friendly AI',
          desc: '22 AI tools cover content, media, and bots without per-client SaaS sprawl.',
        },
      ],
    },
    results: [
      { value: '60%', label: 'Faster launches' },
      { value: '3×', label: 'Clients per pod' },
      { value: '45%', label: 'Higher delivery margin' },
      { value: '1 login', label: 'For the whole stack' },
    ],
    stack: ['White-label', 'Snag Builder', 'AI Tools', 'Marketing Suite', 'CRM'],
    story: {
      company: 'Peakline Digital',
      role: 'Managing Partner',
      quote:
        'We standardized onboarding. Juniors ship first drafts; seniors polish. Clients think we grew the team — we grew the system.',
      outcome: 'Took on 9 new retainers without adding headcount.',
    },
    playbook: [
      { step: '01', title: 'Template the offer', desc: 'Package site + marketing + AI into a clear retainer.' },
      { step: '02', title: 'White-label the workspace', desc: 'Brand the client-facing experience as yours.' },
      { step: '03', title: 'Scale delivery', desc: 'Reuse playbooks across verticals with AI assistance.' },
    ],
  },
  {
    slug: 'coaches',
    industry: 'Coaches & Creators',
    eyebrow: 'Personal brands',
    headline: 'Turn attention into booked calls',
    summary:
      'Landing pages, course-ready sites, AI content, and WhatsApp follow-ups that convert followers into clients.',
    heroAccent: '#fbbf0f',
    metricHighlight: { value: '3.1×', label: 'more booked discovery calls' },
    challenge: {
      title: 'Content without a conversion path is noise',
      body:
        'Creators post daily but lose leads in DMs. Funnels are fragile, and booking + nurture never stay in sync with the brand.',
      pains: [
        'Link-in-bio pages feel cheap next to the content',
        'DMs are the only CRM — and they vanish',
        'Launch creatives take longer than the offer itself',
        'No reliable way to nurture cold followers',
      ],
    },
    solution: {
      title: 'Audience → offer → booked call',
      body:
        `${CASE_STUDY_BRAND} gives coaches a premium site, AI content studio, bots that capture leads, and multi-channel follow-up so every piece of content has a next step.`,
      pillars: [
        {
          title: 'Offer-first websites',
          desc: 'Programs, testimonials, and booking CTAs that match your content brand.',
        },
        {
          title: 'AI content engine',
          desc: 'Scripts, carousels, voiceovers, and thumbnails without a full creative team.',
        },
        {
          title: 'Always-on lead capture',
          desc: 'Smart bots and forms that qualify and book while you teach.',
        },
        {
          title: 'Nurture that feels personal',
          desc: 'WhatsApp and email sequences that sound like you.',
        },
      ],
    },
    results: [
      { value: '3.1×', label: 'Discovery calls booked' },
      { value: '52%', label: 'DM-to-form conversion' },
      { value: '8 hrs', label: 'Saved on content / week' },
      { value: '22%', label: 'Higher program close rate' },
    ],
    stack: ['AI Website Builder', 'AI Tools', 'Bot Leads', 'WhatsApp Marketing', 'CRM'],
    story: {
      company: 'Maya Ellison Coaching',
      role: 'Founder',
      quote:
        'My site finally looks like my Instagram — and the bot books calls while I’m recording. I stopped living in my DMs.',
      outcome: 'Waitlist filled for two cohorts in under a month.',
    },
    playbook: [
      { step: '01', title: 'Ship the offer page', desc: 'AI drafts a conversion-focused program site.' },
      { step: '02', title: 'Wire capture', desc: 'Bots + forms qualify and book discovery calls.' },
      { step: '03', title: 'Nurture at scale', desc: 'WhatsApp and email keep the pipeline warm.' },
    ],
  },
  {
    slug: 'local-services',
    industry: 'Local Services',
    eyebrow: 'Home & field',
    headline: 'Own the neighborhood search — and the inbox',
    summary:
      'Service sites, AI SEO, lead scraping, and appointment bots built for plumbers, salons, gyms, and field teams.',
    heroAccent: '#fbbf0f',
    metricHighlight: { value: '41%', label: 'more qualified inbound leads' },
    challenge: {
      title: 'Local demand is messy and phone-heavy',
      body:
        'Service businesses live on Google, referrals, and missed calls. Without a modern site and follow-up, competitors win the same ZIP codes.',
      pains: [
        'Website looks outdated next to new competitors',
        'Missed calls equal lost jobs',
        'No system for reviews and rebooking',
        'Ads run without a landing page that converts',
      ],
    },
    solution: {
      title: 'Local growth without a marketing hire',
      body:
        `${CASE_STUDY_BRAND} ships service-area sites, AI SEO, voice/chat bots, and outbound lead tools so local operators capture and close demand faster.`,
      pillars: [
        {
          title: 'Service-area sites',
          desc: 'Location pages, galleries, and CTAs tuned for “near me” intent.',
        },
        {
          title: 'Never-miss lead capture',
          desc: 'Voice and chat bots book estimates when you’re on a job.',
        },
        {
          title: 'AI SEO & ads support',
          desc: 'Local content and creative that keep the pipeline full.',
        },
        {
          title: 'Lead scraping to CRM',
          desc: 'Find nearby prospects and nurture them into booked work.',
        },
      ],
    },
    results: [
      { value: '41%', label: 'More qualified leads' },
      { value: '2×', label: 'After-hours bookings' },
      { value: '18%', label: 'Higher close rate' },
      { value: '1 week', label: 'To relaunch the brand site' },
    ],
    stack: ['AI Website Builder', 'AI SEO', 'Bot Leads & Calls', 'Lead Scraper', 'CRM'],
    story: {
      company: 'BrightPath HVAC',
      role: 'Operations Manager',
      quote:
        'The voice bot books after-hours emergency calls. Our site finally ranks for the neighborhoods we actually serve.',
      outcome: 'Cut missed-call losses and filled the weekly calendar.',
    },
    playbook: [
      { step: '01', title: 'Relaunch locally', desc: 'Service pages + proof for every ZIP you cover.' },
      { step: '02', title: 'Catch every call', desc: 'Bots and forms capture jobs 24/7.' },
      { step: '03', title: 'Fill the calendar', desc: 'SEO, ads, and outbound keep demand steady.' },
    ],
  },
  {
    slug: 'real-estate',
    industry: 'Real Estate',
    eyebrow: 'Property',
    headline: 'Listings that look premium — and convert faster',
    summary:
      'Agent sites, AI property media, lead capture, and nurture sequences for brokerages and independent agents.',
    heroAccent: '#fbbf0f',
    metricHighlight: { value: '2×', label: 'faster listing-to-showing' },
    challenge: {
      title: 'Listings expire before the funnel does',
      body:
        'Agents juggle MLS exports, Canva decks, WhatsApp groups, and CRMs that nobody updates. Hot leads go cold between open houses.',
      pains: [
        'Listing pages take too long to look premium',
        'Lead follow-up is inconsistent across agents',
        'Creative for launches is always last-minute',
        'No clear pipeline from inquiry to showing',
      ],
    },
    solution: {
      title: 'A modern desk for modern listings',
      body:
        `${CASE_STUDY_BRAND} helps agents publish listing sites, generate media, capture inquiries, and automate nurture so every lead gets a next step.`,
      pillars: [
        {
          title: 'Listing microsites',
          desc: 'Beautiful property pages with galleries, maps, and inquiry CTAs.',
        },
        {
          title: 'AI media kits',
          desc: 'Stylized visuals, reels scripts, and social packs for every launch.',
        },
        {
          title: 'Lead routing',
          desc: 'Bots and forms qualify buyers and sellers into CRM stages.',
        },
        {
          title: 'Nurture sequences',
          desc: 'WhatsApp and email keep prospects warm between showings.',
        },
      ],
    },
    results: [
      { value: '2×', label: 'Faster listing launch' },
      { value: '35%', label: 'More qualified inquiries' },
      { value: '24%', label: 'Higher showing rate' },
      { value: '10 hrs', label: 'Saved per agent / week' },
    ],
    stack: ['AI Website Builder', 'Media Studio', 'CRM', 'WhatsApp Marketing', 'AI Tools'],
    story: {
      company: 'Vista & Co. Realty',
      role: 'Broker-owner',
      quote:
        'New listings go live the same afternoon with a full media kit. Agents finally follow up because the CRM is simple.',
      outcome: 'Standardized every agent’s digital presence in one quarter.',
    },
    playbook: [
      { step: '01', title: 'Publish listings fast', desc: 'AI site + media kit for each property.' },
      { step: '02', title: 'Capture every inquiry', desc: 'Forms and bots feed a shared pipeline.' },
      { step: '03', title: 'Nurture to showing', desc: 'Automated follow-ups keep deals moving.' },
    ],
  },
  {
    slug: 'healthcare',
    industry: 'Clinics & Wellness',
    eyebrow: 'Care practices',
    headline: 'A calm digital front desk for busy clinics',
    summary:
      'Trust-building sites, appointment bots, patient education content, and reminder campaigns — without looking clinical-cold.',
    heroAccent: '#fbbf0f',
    metricHighlight: { value: '29%', label: 'fewer no-shows' },
    challenge: {
      title: 'Patients decide before they ever call',
      body:
        'Clinics lose trust on outdated sites and miss appointments to voicemail. Staff burn hours on reminders instead of care.',
      pains: [
        'Website doesn’t communicate expertise or warmth',
        'Booking friction drives patients elsewhere',
        'Reminder calls eat front-desk time',
        'Educational content is inconsistent',
      ],
    },
    solution: {
      title: 'Care-first digital experience',
      body:
        `${CASE_STUDY_BRAND} helps clinics present a polished brand, automate booking and reminders, and publish patient education with AI — so the front desk stays focused on people.`,
      pillars: [
        {
          title: 'Trust-first websites',
          desc: 'Provider stories, services, and FAQs that feel human and clear.',
        },
        {
          title: 'Smart booking capture',
          desc: 'Bots qualify and schedule without phone-tag.',
        },
        {
          title: 'Reminder journeys',
          desc: 'WhatsApp and email reduce no-shows automatically.',
        },
        {
          title: 'AI education content',
          desc: 'Articles and explainers that build authority between visits.',
        },
      ],
    },
    results: [
      { value: '29%', label: 'Fewer no-shows' },
      { value: '44%', label: 'More online bookings' },
      { value: '15 hrs', label: 'Front-desk time saved / week' },
      { value: '4.9★', label: 'Patient digital experience' },
    ],
    stack: ['AI Website Builder', 'Bot Leads', 'WhatsApp Marketing', 'Email Marketing', 'AI Tools'],
    story: {
      company: 'Lumen Wellness Clinic',
      role: 'Practice Manager',
      quote:
        'Patients book after hours, reminders go out automatically, and our site finally matches the care we give in person.',
      outcome: 'Filled the schedule without adding front-desk staff.',
    },
    playbook: [
      { step: '01', title: 'Rebuild trust online', desc: 'A warm, clear practice site with services and providers.' },
      { step: '02', title: 'Open digital booking', desc: 'Bots and forms capture appointments 24/7.' },
      { step: '03', title: 'Protect the schedule', desc: 'Reminders and education keep patients engaged.' },
    ],
  },
  {
    slug: 'startups',
    industry: 'Startups',
    eyebrow: 'Early teams',
    headline: 'Ship the company OS before you hire marketing',
    summary:
      'Landing pages, AI product storytelling, outbound leads, and automations so founders stay focused on product.',
    heroAccent: '#fbbf0f',
    metricHighlight: { value: '10 days', label: 'from idea to live GTM kit' },
    challenge: {
      title: 'Early teams drown in tool sprawl',
      body:
        'Founders duct-tape Notion, Canva, Mailchimp, and a half-built site. Launch energy goes into tooling instead of customers.',
      pains: [
        'Landing page never feels launch-ready',
        'No time for consistent outbound or content',
        'Every experiment needs another SaaS seat',
        'Investor and customer stories look unfinished',
      ],
    },
    solution: {
      title: 'GTM infrastructure in one workspace',
      body:
        `${CASE_STUDY_BRAND} gives startups a fast site, AI content, lead tools, and automations so a tiny team can look and operate like a grown-up company.`,
      pillars: [
        {
          title: 'Launch-ready sites',
          desc: 'Ship a crisp narrative site and iterate with AI in days.',
        },
        {
          title: 'Story & creative AI',
          desc: 'Decks, posts, product visuals, and copy from one studio.',
        },
        {
          title: 'Outbound that compounds',
          desc: 'Lead scraping + CRM keep the top of funnel alive.',
        },
        {
          title: 'Automations early',
          desc: 'Nurture and ops workflows without a RevOps hire.',
        },
      ],
    },
    results: [
      { value: '10 days', label: 'Idea → GTM kit' },
      { value: '70%', label: 'Fewer tools in stack' },
      { value: '3×', label: 'Outbound volume' },
      { value: '$0', label: 'Extra seats for core AI' },
    ],
    stack: ['Snag Builder', 'AI Tools', 'Lead Scraper', 'CRM', 'Automations'],
    story: {
      company: 'Relayfield',
      role: 'Co-founder',
      quote:
        'We launched the site, outbound sequences, and investor one-pager in the same week — without pausing product work.',
      outcome: 'Closed first 20 design partners with a two-person team.',
    },
    playbook: [
      { step: '01', title: 'Ship the narrative', desc: 'AI site + brand kit that explain the product clearly.' },
      { step: '02', title: 'Open the pipeline', desc: 'Lead tools and CRM capture early demand.' },
      { step: '03', title: 'Automate the grind', desc: 'Sequences free founders to talk to customers.' },
    ],
  },
];

export function getCaseStudyBySlug(slug) {
  return industryCaseStudies.find((c) => c.slug === slug) || null;
}

export function getRelatedCaseStudies(slug, limit = 3) {
  return industryCaseStudies.filter((c) => c.slug !== slug).slice(0, limit);
}
